import { AnimatePresence, motion } from 'framer-motion';

/** Combo timeout window in seconds (matches `comboTimeout = 1.5` in
 *  GameEngine onMatchEat). Used to normalize the timer bar to 0..1. */
const COMBO_WINDOW = 1.5;

/**
 * Combo streak badge — restrained entry + a tiny countdown bar that
 * shows how much of the combo window remains. The bar collapses
 * smoothly from 100% → 0% over 1.5 seconds so the player can SEE
 * how urgent the next eat is.
 *
 * Each new combo step still triggers a fresh enter via `key={combo}`,
 * but the animation is fast + quiet — a long streak shouldn't feel like
 * a fireworks display.
 */
export function ComboBadge({
  combo,
  playerHue,
  comboRemaining,
}: {
  combo: number;
  playerHue: number;
  comboRemaining: number;
}) {
  const visible = combo >= 2;
  const color = `hsl(${playerHue} 100% 65%)`;
  const accent = `hsl(${(playerHue + 40) % 360} 100% 65%)`;
  const pct = Math.max(0, Math.min(1, comboRemaining / COMBO_WINDOW));

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          // key=combo on the outer wrapper re-runs entry on each step.
          // The timer bar uses CSS transition so it stays smooth between
          // patches (HUD pushes every 100ms — without easing the bar
          // would step visibly). The 124px offset clears the maximum
          // possible IdentityCard height (header + boost meter + best/
          // kills row + powerup chip row) so the badge never overlaps
          // the chips when multiple powerups are active.
          key={combo}
          className="absolute left-3 top-[124px] pointer-events-none overflow-hidden rounded"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.12 } }}
          transition={{ type: 'spring', stiffness: 600, damping: 32 }}
          style={{
            background: `linear-gradient(135deg, ${color}, ${accent})`,
            color: '#06080f',
            boxShadow: `0 0 8px ${color}66`,
          }}
        >
          <div className="px-2 py-0.5 text-[10px] font-semibold tracking-[0.15em] tabular-nums">
            {combo}× COMBO
          </div>
          {/* Countdown bar — shrinks horizontally as the combo window
              elapses. Bright bar against the gradient background. */}
          <div
            className="h-[2px] origin-left transition-[width] duration-100 ease-linear"
            style={{
              width: `${pct * 100}%`,
              background: 'rgba(255,255,255,0.85)',
              boxShadow: '0 0 4px rgba(255,255,255,0.6)',
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
